import { useState, useEffect, useCallback } from 'react';
import { cookieConsentService } from '../services/cookieConsent.service';
import { marketingService } from '../services/marketing.service';
import type { LocalCookiePreference } from '../types/cookieConsent.types';

interface UseCookieConsentReturn {
  preference: LocalCookiePreference | null;
  showBanner: boolean;
  activePolicyVersion: string | null;
  acceptAll: (version: string) => void;
  acceptEssentialOnly: (version: string) => void;
  updatePreference: (marketing: boolean, version: string) => void;
}

export function useCookieConsent(): UseCookieConsentReturn {
  const [preference, setPreference] = useState<LocalCookiePreference | null>(
    cookieConsentService.getPreference()
  );
  const [activePolicyVersion, setActivePolicyVersion] = useState<string | null>(null);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    cookieConsentService.fetchActivePolicyVersion().then((version) => {
      setActivePolicyVersion(version);

      const current = cookieConsentService.getPreference();
      if (!current || !version || current.version !== version) {
        setShowBanner(true);
      } else {
        setShowBanner(false);
        if (current.marketing) {
          marketingService.init(current.marketing);
        }
      }
    });
  }, []);

  const applyPreference = useCallback((marketing: boolean, version: string) => {
    const saved = cookieConsentService.setPreference(marketing, version);
    setPreference(saved);
    setShowBanner(false);
    marketingService.init(marketing);
  }, []);

  const acceptAll = useCallback((version: string) => {
    applyPreference(true, version);
  }, [applyPreference]);

  const acceptEssentialOnly = useCallback((version: string) => {
    applyPreference(false, version);
  }, [applyPreference]);

  const updatePreference = useCallback((marketing: boolean, version: string) => {
    applyPreference(marketing, version);
  }, [applyPreference]);

  return { preference, showBanner, activePolicyVersion, acceptAll, acceptEssentialOnly, updatePreference };
}
